import classNames from 'classnames';
import { PageInfo } from 'iframe-resizer';
import {
  CSSProperties,
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useMeasure, { UseMeasureResult } from 'react-use/esm/useMeasure';

import Layer from './layers/Layer';
import OpenableLayer from './layers/OpenableLayer';
import Popover from './popovers/Popover';
import PopoverOpenButton from './popovers/PopoverOpenButton';
import classes from './App.module.scss';
import iFrameResizerChild from './iframes/iFrameResizerChild';
import interposerLayer from './layers/interposerLayer';
import qubitsLayer from './layers/qubitsLayer';
import resonatorsLayer from './layers/resonatorsLayer';
import shieldLayer from './layers/shieldLayer';
import wiringLayer from './layers/wiringLayer';

// TODO #1 Doc: Finální zpracování responzivních popover:
// - Oddělený anchor pro open button a pro popover s close buttonem.
// - Animace zobrazení popover bude začínat na 0 x 0px.
// - Na nižších rozlišeních se popover anchor přesune do levého / pravého dolního rohu.
//   Open button zůstává na svém místě.
//   Kvůli tomu bude pravděpodobně nutné vytvořit další anchor, který by byl pozicovaný relativně k ColumnContainer.
// - Pokud by ani to nestačilo, tak ještě navíc posunout vrstvy čipu na horní okraj ColumnContainer.
// TODO #1 Doc: Jak zobrazit popover:
// - Jako nejlepší varianta se zdá zarovnání dolů a na vnější stranu. Tak je jsou viditelné všechny
//   vrstvy a zároveň je zachováná pěkná animace, kdy popover vychází z tlačítka pro otevření.
//   - Možná by zde ještě mohlo být něco jako mix-max šířka, kdy by byl levý okraj popoveru na levém okraji sloupce a pravý okraj na open tlačítku.
//   - Také by šla připravit další varianta, kdy by došlo k překlopení nahoru, pokud by dole nebyl dostatek místa.
// - Záložní variantou je převrácení popoveru na vnitřní stranu. Pěkná animace ja zachovaná, ale chip jde méně vidět.
//   - Bezpodmínečně musí být dost prostoru dole.
//   - Popover nesmí moc zakrývat čip - do 1/2 asi bez problému, možná i do 2/3.
/**
 * Layout of the open chip, which naming is relative to the place where the popover is positioned.
 * Following layouts are sorted from the most favorable:
 *
 * - `outside-down` - Popover is positioned outside of the chip and bellow the open button.
 * - `outside-up` - Popover is positioned outside of the chip and above the open button.
 * - `inside` - Popover is positioned inside of the chip and bellow the open button.
 * - `bottom` - Popover is positioned at the bottom of the page with the same X as the open button.
 * - `corner` - Popover is positioned at the corner of the page (left or right).
 */
// type Layout = 'outside-down' | 'outside-up' | 'inside' | 'bottom' | 'corner';

/**
 * Fraction of the chip height where the chip starts to expand when touched by the bottom
 * of the window.
 */
const collapsedScrollY = 0.6;

/** Fraction by which is the chip expanded on each animation frame. */
const expandSpeed = 0.04;

/** Fraction of the chip height where the expanding takes place. */
const expandScrollHeight = 0.2;

// TODO #1 Move to CSS?
const layerOpenScale = 1.65;

// TODO #1 Pass to CSS or load from CSS?
const popoverWidth = 320 - 2 * 16;

// TODO #1 Pass to CSS
const popoverOffset = -22 / 2;

/**
 * Maximal fraction of the chip width, that can be covered by the popover. Value is relative
 * to the side to which is the popover positioned. If the popover exceeds this width it's
 * reffered to as an overflowing popover.
 */
const popoverBreakpoint = 0.5;

// TODO #1 Doc: Optimize performance by creating div for each layer and only translate this div.
// TODO #1 Define descriptions in the text files to prevent long lines and useless whitespace in strings?
const layers: Layer[] = [interposerLayer, wiringLayer, resonatorsLayer, qubitsLayer, shieldLayer];

const openableLayers: OpenableLayer[] = layers.filter(
  (layer): layer is OpenableLayer => layer.type === 'openable',
);

const getExpandOffsetY = (expandedFraction: number, layer: Layer) =>
  `${layer.collapsedOffsetY * (1 - expandedFraction) * 100}%`;

const getChipAnchorStyle = (layer: OpenableLayer, isExpanded: boolean, openLayer?: OpenableLayer) =>
  ({
    // Hide the anchor if the chip isn't fully expanded, or another layer is selected.
    '--opacity': !isExpanded || (openLayer != null && layer !== openLayer) ? 0 : 1,
    // Position the anchor to the layer and move it as the chip expands.
    '--top': `${layer.anchorPositionY * 100}%`,
    '--left': `${layer.anchorPositionX * 100}%`,
  } as CSSProperties);

/**
 * Fraction of how much is the popover overflowing to breakpoint. Zero means no overflow
 * and one means full overflow with popover on full width.
 */
const getSideAnchorOverflow = (columnWidth: number) => {
  // Prevent division by zero.
  if (columnWidth === 0) {
    return 0;
  }
  // Fraction of the width of the chip that is the popover covering.
  // TODO #1 Account the chip size, not the column width for applying, as there might be a spacing.
  // - This isn't noticable with breakpoint in the middle.
  const widthFraction = popoverWidth / columnWidth;
  // Distance to breakpoint as a fraction of chip size.
  return Math.max(0, widthFraction - popoverBreakpoint) / (1 - popoverBreakpoint);
};

/** Gets the side anchor top position. */
const getSideAnchorTop = (
  columnWidth: number,
  columnHeight: number,
  layer: OpenableLayer,
  popoverHeight: number,
) => {
  // Prevent division by zero.
  if (columnHeight === 0) {
    return 0;
  }
  // Fraction of the height of the chip that is the popover covering.
  const heightFraction = popoverHeight / columnHeight;
  // Get the top-most vertical position of the popover with popover positioned from the bottom.
  // TODO #1 Account the popover offset, which has the absolute width.
  const minY = Math.max(0, Math.min(1, layer.anchorPositionY + heightFraction));
  // Bottom-most vertical position of the popover.
  const maxY = 1;
  // Get the popover overflow fraction.
  const overflow = getSideAnchorOverflow(columnWidth);
  // Move the popover to the bottom as it overflows the chip.
  return minY + overflow * (maxY - minY);
};

const getSideAnchorStyle = (
  columnWidth: number,
  columnHeight: number,
  layer: OpenableLayer,
  isExpanded: boolean,
  popoverHeight: number,
  openLayer?: OpenableLayer,
) =>
  ({
    // Hide the anchor if the chip isn't fully expanded, or another layer is selected.
    '--opacity': !isExpanded || (openLayer != null && layer !== openLayer) ? 0 : 1,
    // Move the anchor to the bottom as its popover overflows the chip.
    '--top': `${getSideAnchorTop(columnWidth, columnHeight, layer, popoverHeight) * 100}%`,
    // Display the popover anchor on the same side as the chip anchor, but in the corner 0% or 100%.
    '--left': `${Math.round(layer.anchorPositionX) * 100}%`,
  } as CSSProperties);

const getChipPopoverPosition = (
  columnHeight: number,
  columnWidth: number,
  layer: OpenableLayer,
  popoverHeight: number,
) => {
  const chipSize = Math.min(columnWidth, columnHeight);
  const spaceX = (columnWidth - chipSize) / 2 - popoverOffset;
  const spaceY = (columnHeight - chipSize) / 2 - popoverOffset;
  const { anchorPositionX, anchorPositionY } = layer;
  const topSpace = chipSize * anchorPositionY + spaceY;
  const bottomSpace = chipSize * (1 - anchorPositionY) + spaceY;
  const leftSpace = chipSize * anchorPositionX + spaceX;
  const rightSpace = chipSize * (1 - anchorPositionX) + spaceX;
  // TODO #1 DRY both if/else blocks are just flipped by the side.
  if (Math.round(anchorPositionX) === 1) {
    if (popoverWidth <= rightSpace) {
      if (popoverHeight <= bottomSpace) {
        return 'bottom-right';
      }
      if (popoverHeight <= topSpace) {
        return 'top-right';
      }
    }
    if (popoverWidth <= leftSpace - chipSize * popoverBreakpoint) {
      return 'bottom-left';
    }
    return undefined;
  }
  if (popoverWidth <= leftSpace) {
    if (popoverHeight <= bottomSpace) {
      return 'bottom-left';
    }
    if (popoverHeight <= topSpace) {
      return 'top-left';
    }
  }
  if (popoverWidth <= rightSpace - chipSize * popoverBreakpoint) {
    return 'bottom-right';
  }
  return undefined;
};

const getChipContainerStyle = (
  columnHeight: number,
  columnWidth: number,
  isExpanded: boolean,
  popoverMeasures: UseMeasureResult[],
  openLayer?: OpenableLayer,
) => {
  // Don't transform the chip container if chip isn't expanded or there is no open layer.
  if (!isExpanded || openLayer == null) {
    return undefined;
  }
  const openLayerIndex = openableLayers.indexOf(openLayer);
  if (openLayerIndex === -1) {
    throw new Error('Unknown open layer');
  }
  const popoverHeight = popoverMeasures[openLayerIndex][1].height;
  // Don't transform the chip container if popover can be positioned on the chip.
  const popoverPosition = getChipPopoverPosition(
    columnHeight,
    columnWidth,
    openLayer,
    popoverHeight,
  );
  if (popoverPosition != null) {
    return undefined;
  }
  // Original chip size is square that is contained inside column container.
  const chipSize = Math.min(columnWidth, columnHeight);
  // Open chip needs to have space reserved for popover underneath it. Compute the chip size for
  // this smaller area.
  const openChipSize = Math.min(columnWidth, columnHeight - popoverHeight);
  // Target minimal scale by which is the open chip smaller than original.
  const minScale = openChipSize / chipSize;
  // Starting maximal scale, which will be lowered to the target scale.
  const maxScale = 1;
  // Get the popover overflow fraction.
  const overflow = getSideAnchorOverflow(columnWidth);
  // Scale the chip down as the popover overflows the chip.
  const scale = maxScale - overflow * (maxScale - minScale);
  // Available space underneath the open chip if it is centered.
  const spaceY = (columnHeight - openChipSize) / 2;
  // Offset by which the chip needs to be translated, so there is enough space for the popover.
  // Again, offset is smoothly increased as the popover overflows.
  const offsetY = overflow * Math.min(0, spaceY - popoverHeight);
  // Return values with CSS units.
  return {
    '--offsetY': `${offsetY}px`,
    '--scale': scale,
  } as CSSProperties;
};

const getOpenContainerClassName = (
  layer: Layer,
  isExpanded: boolean,
  openLayer?: OpenableLayer,
) => {
  const hasOpenLayer = openLayer != null;
  const openLayerIndex = hasOpenLayer ? layers.indexOf(openLayer) : -1;
  const isOpenLayer = layer === openLayer;
  const layerIndex = layers.indexOf(layer);
  const isAnotherLayerOpen = isExpanded && hasOpenLayer && !isOpenLayer;
  return classNames(classes.openContainer, {
    [classes.openContainer_hidden]: isAnotherLayerOpen && layerIndex > openLayerIndex,
    [classes.openContainer_faded]: isAnotherLayerOpen && layerIndex < openLayerIndex,
  });
};

const getOpenContainerStyle = (isExpanded: boolean, layer: Layer, openLayer?: OpenableLayer) =>
  ({
    // Move the plates and arrows as the chip expands.
    '--offsetY': `${(layer === openLayer && isExpanded ? layer.openOffsetY : 0) * 100}%`,
    '--scale': layer === openLayer && isExpanded ? layerOpenScale : 1,
  } as CSSProperties);

// TODO #1 Doc: Improve chip offsets for expanding (only small landscape resolution missing):
// - The user should see the chip collapsed.
//   - Chip should start expanding, when the whole (or most of) collapsed chip is visible (= hits the bottom of the viewport).
//   - `collapsedScrollY` = fraction of the height of the chip, when the layers should start to expand.
// - On small landscape resolution the chip isn't visible on full height.
//   - Chip must be fully expanded when the top of the viewport hits expanded chip top.
//   - `collapsedScrollY` are adjusted by the `expandedScrollHeight`, so this condition is satisfied.
// - On small portrait resolution there is the top padding, which should be ignored.
//   -  Adjust `scrollTop` by this value.
// - Distance needed to scroll to fully expand the chip should be the same for all strategies.
//   - Maybe also limit this to like half of the chip?
//   - `expandedScrollHeight` = fraction of the height of the chip, where the expanding should happen as the page `scrollTop` is inside this height.
const getExpandedFraction = (columnWidth: number, columnHeight: number, pageInfo: PageInfo) => {
  const { offsetTop, scrollTop, windowHeight } = pageInfo;
  // Chip size is square that is contained inside column container.
  const chipSize = Math.min(columnWidth, columnHeight);
  // Available space above the chip if it is centered.
  const spaceY = (columnHeight - chipSize) / 2;
  // TODO #1 Doc: portrait resolution
  const bottomExpandedRatio =
    (scrollTop + windowHeight - (offsetTop + spaceY + collapsedScrollY * chipSize)) /
    (expandScrollHeight * chipSize);
  // TODO #1 Doc: small landscape resolution resolution
  const topExpandedRatio =
    (scrollTop + expandScrollHeight * chipSize - (offsetTop + spaceY)) /
    (expandScrollHeight * chipSize);
  const newExpandedRatio = Math.max(bottomExpandedRatio, topExpandedRatio);
  return Math.max(0, Math.min(1, newExpandedRatio));
};

const getArrowsImageStyle = (isExpanded: boolean, layer: Layer, openLayer?: OpenableLayer) =>
  // Start displaying the arrows when the chip is half visible, so they aren't visible above
  // the collapsed chip. Also hide arrows when the layer is opened.
  ({ '--opacity': isExpanded && layer !== openLayer ? 1 : 0 } as CSSProperties);

// TODO #1 Check the production optimization (source maps, class names, ...)
// TODO #1 Doc: Responsivity must be handled by the parent, as the browser width isn't accessible.
// - Display iframe on full width?
// - How to change the iframe height?
//   - Define height for each breakpoint and set it outside iframe.
//   - Define aspect ratio.
//   - Send request to change height over iframe messaging.
//   - Or, it's not requested: Headline width is 16px from close icon. Copy spans full width.
//     Still waiting on final copy so this may change. Either clicking on the close icon, box
//     (hovering on box maybe we show hover on close icon) or outside with close text box and return
//     to full chip with blue buttons
//     - But he sm resolution design spec are smaller in height.
// TODO #1 Refactor App to smaller modules.
// TODO #1 Doc everywhere
// TODO #1 Use rem instead of px?
// TODO #1 Why isn't Husky validation working?
// - "postinstall": "husky install"
// - https://github.com/typicode/husky/issues/719
// TODO #1 Doc: All values are in fractions, not percentages for easier manipulation.
const App: FC = () => {
  /** Flag, that determines if the chip layers are fully expanded. */
  const [isExpanded, setIsExpanded] = useState(false);
  /** Currently open layer. Stays open on background even if the layers are collapsed. */
  const [openLayer, setOpenLayer] = useState<OpenableLayer | undefined>();

  const [columnRef, { width: columnWidth, height: columnHeight }] = useMeasure();

  // This is safe to do, layers won't ever change and the call order will remain the same.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const expandContainerRefs = layers.map(() => useRef<HTMLDivElement>(null));

  const popoverMeasures = openableLayers.map(useMeasure);

  const handlePopoverClose = useCallback(() => setOpenLayer(undefined), []);

  // Animate the chip expansion outside React render cycle, for the best performance.
  useEffect(() => {
    /**
     * Expanded fraction of the chip, that is currently displayed. The initial animation state
     * is synced with the React state.
     */
    let currentExpanded = isExpanded ? 1 : 0;
    /** Request animation frame identifier. Set to the identifier of the last request. */
    let animationId: number | undefined;
    /**
     * Expanded fraction of the chip, that should be displayed according to the current scroll
     * position.
     */
    let targetExpanded = NaN;
    /**
     * Flag specifies if there is some animation running, to prevent from having more than one
     * active animation frame request.
     */
    let isAnimating = false;
    /** Apply the expanded fraction on the expand container elements. */
    const render = (expandedFraction: number) => {
      layers.forEach((layer, index) => {
        const expandContainer = expandContainerRefs[index].current;
        const offsetY = getExpandOffsetY(expandedFraction, layer);
        expandContainer.style.setProperty('--offsetY', offsetY);
      });
    };
    /** Handle the animation frame, where is the expand animation applied. */
    const handleAnimationFrame = () => {
      // Stop the animation if the target expanded fraction is reached.
      if (currentExpanded === targetExpanded) {
        isAnimating = false;
        return;
      }
      // Remaining expanded fraction difference.
      const expandedDelta = currentExpanded - targetExpanded;
      // Compute the next animation step that lowers the difference. Use the requested distance,
      // or the remaining difference between the current and the target expanded fraction.
      const newCurrentExpanded =
        currentExpanded - Math.sign(expandedDelta) * Math.min(expandSpeed, Math.abs(expandedDelta));
      // Immediately apply the new expanded fraction step on the expand containers.
      render(newCurrentExpanded);
      // Replace the current expanded state by the new one.
      currentExpanded = newCurrentExpanded;
      // Request the next step of the animation.
      animationId = requestAnimationFrame(handleAnimationFrame);
      // Sync the React state, which is only fully open or not. React doesn't know the exact
      // expanded fraction, as it would require re-render to change it. Calling this inside rAF
      // is dangerous, but the boolean value won't produce the unnecessary re-renders, unless
      // it changes. It shouldn't change frequently, only once for each scroll direction and when
      // a certain scroll offset is reached.
      const newIsExpanded = newCurrentExpanded === 1;
      setIsExpanded(newIsExpanded);
    };
    /** Handle the page info change, that determines how much should be the chip expanded. */
    const handlePageInfo = pageInfo => {
      const { iframeHeight, offsetTop, scrollTop, windowHeight } = pageInfo;
      // Don't animate when not scrolled into view (above or bellow), the chip isn't visible.
      if (scrollTop <= offsetTop - windowHeight || scrollTop >= offsetTop + iframeHeight) {
        return;
      }
      // Update how much should be the chip expanded according to the new page sizing.
      // Animation will smoothly expand the chip to this value.
      const newTargetExpanded = getExpandedFraction(columnWidth, columnHeight, pageInfo);
      targetExpanded = newTargetExpanded;
      // If there is no animation running and there is some difference in expanded values,
      // start the new animation to smoothly change the animation postition to a target position.
      if (!isAnimating && currentExpanded !== newTargetExpanded) {
        isAnimating = true;
        animationId = requestAnimationFrame(handleAnimationFrame);
      }
    };
    // Immediately apply the initial expanded fraction.
    render(currentExpanded);
    // Handle the initial page info, if the parent page already sent one.
    const initialPageInfo = iFrameResizerChild.getCurrentPageInfo();
    if (initialPageInfo != null) {
      handlePageInfo(initialPageInfo);
    }
    // Start listening to the page info changes.
    iFrameResizerChild.on('pageInfo', handlePageInfo);
    return () => {
      iFrameResizerChild.off('pageInfo', handlePageInfo);
      cancelAnimationFrame(animationId);
    };
    // Hotfix for using array of refs. Refs order won't change, so it's safe to do.
    // Also, `isExpanded` is omitted, to prevent useless useEffect activation, as this is the only
    // place where the `isExpanded` is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnHeight, columnWidth, ...expandContainerRefs]);

  const anchorComponents = useMemo(
    () =>
      openableLayers.reduce(
        (acc, layer, index) => {
          const [ref, { height: popoverHeight }] = popoverMeasures[index];
          const popoverPosition = getChipPopoverPosition(
            columnHeight,
            columnWidth,
            layer,
            popoverHeight,
          );
          if (popoverPosition == null) {
            acc.column.push(
              <div
                className={classNames(classes.anchorContainer)}
                // Anchors are in the immutable array. It's safe to use index to identify them.
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                style={getSideAnchorStyle(
                  columnWidth,
                  columnHeight,
                  layer,
                  isExpanded,
                  popoverHeight,
                  openLayer,
                )}
              >
                <div className={classNames(classes.anchor)}>
                  <Popover
                    header={layer.name}
                    isOpen={layer === openLayer}
                    onClose={handlePopoverClose}
                    position={Math.round(layer.anchorPositionX) === 1 ? 'top-left' : 'top-right'}
                    ref={ref}
                    text={layer.description}
                  />
                </div>
              </div>,
            );
          }
          // Chip anchors are in the reversed order, so they are focused from top to bottom.
          acc.chip.unshift(
            <div
              className={classes.anchorContainer}
              // Anchors are in the immutable array. It's safe to use index to identify them.
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={getChipAnchorStyle(layer, isExpanded, openLayer)}
            >
              <div className={classNames(classes.anchor)}>
                <PopoverOpenButton
                  description={layer.description}
                  isHidden={layer === openLayer}
                  onClick={() => setOpenLayer(layer)}
                />
                {popoverPosition != null && (
                  <Popover
                    header={layer.name}
                    isOpen={layer === openLayer}
                    onClose={handlePopoverClose}
                    offset={popoverOffset}
                    position={popoverPosition}
                    ref={ref}
                    text={layer.description}
                  />
                )}
              </div>
            </div>,
          );
          return acc;
        },
        {
          chip: [] as ReactElement[],
          column: [] as ReactElement[],
        },
      ),
    [columnHeight, columnWidth, isExpanded, handlePopoverClose, openLayer, popoverMeasures],
  );

  return (
    <div className={classes.fullscreenContainer}>
      <div className={classes.pageContainer}>
        <div className={classes.columnContainer} ref={columnRef}>
          <div className={classes.sizeContainer}>
            <div
              className={classNames(classes.chipContainer)}
              style={getChipContainerStyle(
                columnHeight,
                columnWidth,
                isExpanded,
                popoverMeasures,
                openLayer,
              )}
            >
              {layers.map((layer, index) => (
                <div
                  className={classes.expandContainer}
                  // Layers are in the immutable array. It's safe to use index to identify them.
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  ref={expandContainerRefs[index]}
                >
                  <div
                    className={getOpenContainerClassName(layer, isExpanded, openLayer)}
                    style={getOpenContainerStyle(isExpanded, layer, openLayer)}
                  >
                    <layer.PlaneImage className={classNames(classes.image)} />
                    {layer.ArrowsImage != null && (
                      <layer.ArrowsImage
                        className={classNames(classes.image)}
                        style={getArrowsImageStyle(isExpanded, layer, openLayer)}
                      />
                    )}
                  </div>
                </div>
              ))}
              {anchorComponents.chip}
            </div>
          </div>
          {anchorComponents.column}
        </div>
      </div>
    </div>
  );
};

export default App;
